import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import {
  AskAiInput,
  CODE_ADMIN,
  DataScope,
  DefaultDataProcessingFeatures,
  DefaultDataScope,
  IQAiResponse,
  IQBoostCreateCRMAction,
  IQChatGroup,
  IQBoostSavedConfigurationAdmin,
  IQBoostSavedConfigurationUser,
  IQDataProcessingFeaturesGroup,
  ScopeChangeRequest,
  StreamingPhase,
  ViewType,
} from '../classes/iqBoost';
import {
  ConfigurationSetting,
  ConfigurationSettingCodes,
  EntryType,
  OpenAI,
  Session,
} from '@maximizer/core/shared/domain';
import { Subject } from 'rxjs';
import { TextPrompt } from 'langfuse-core';
import { OpenEntityArgs } from '@maximizer/core/shared/ui/config';
import { SHA256 } from 'crypto-es';
import { IqBoostPrompt, IqBoostPromptCategory } from '../types/iqboost-prompts.types';
import { isNullOrEmpty, sanitizeMarkdownToHtml } from '@maximizer/shared/util';

export type IQBoostViewState = {
  loading: boolean;
  streaming: boolean;
  streamingPhase: StreamingPhase | null;
  viewType: ViewType;
  key: string | undefined;
  type: EntryType | undefined;
  entryDisplayValue: string;
  opportunityDisplayValue: string;
  currentPrompt: IqBoostPrompt | undefined;
  systemPrompts: TextPrompt[];
  chatGroups: IQChatGroup[];
  /** Scope/privacy/household change: refresh the scoped prompt catalog, then re-run the current prompt. */
  triggerScopeChangeSubject: Subject<ScopeChangeRequest>;
  triggerAskAiSubject: Subject<AskAiInput>;
  responseFromAiSubject: Subject<string>;
  streamingResponseFromAiSubject: Subject<string>;
  streamingResponseCompleteSubject: Subject<void>;
  cancelInFlightSubject: Subject<void>;
  triggerShowConfirmationDialogSubject: Subject<string>;
  triggerOpenEntitySubject: Subject<OpenEntityArgs>;
  triggerSendSuggestionSubject: Subject<void>;
  sessionInfo: Session | undefined;
  dataScope: DataScope;
  sidebarMenuCollapsed: boolean;
  useAllHouseholds: boolean;
  promptCategories: IqBoostPromptCategory[];
  aiResponse: IQAiResponse;
  dataProcessingFeatureGroup: IQDataProcessingFeaturesGroup;
  anonymizedInAnimation: {
    animate: boolean;
    anonymized: boolean;
  };
  confirmCreateCRMAction: IQBoostCreateCRMAction;
  configurationSettingCodesUser: ConfigurationSettingCodes;
  configurationSettingCodesAdmin: ConfigurationSettingCodes;
  configurationSettingUser: ConfigurationSetting | null;
  configurationSettingAdmin: ConfigurationSetting | null;
  feedbackGivenForResponses: Map<string, boolean>;
  showErrorMessageDialog: boolean;
  widgetIsExpanded: boolean;
  accuracyBannerVisible: boolean;
  outsideQuestion: string | undefined;
}

export function createDefaultIqBoostUserSavedConfiguration(): IQBoostSavedConfigurationUser {
  return {
    wasFirstTimeOpenedPrivacySecurityCenter: false,
    dataProcessingFeatures: DefaultDataProcessingFeatures,
    dataScope: DefaultDataScope,
    recentUserPrompts: [],
  };
}

export function createDefaultIqBoostAdminSavedConfiguration(): IQBoostSavedConfigurationAdmin {
  return { dataProcessingFeatures: DefaultDataProcessingFeatures };
}

const initialState: IQBoostViewState = {
  loading: false,
  streaming: false,
  streamingPhase: null,
  viewType: ViewType.Report,
  key: '',
  type: 'addressBook',
  entryDisplayValue: '',
  opportunityDisplayValue: '',
  currentPrompt: undefined,
  systemPrompts: [],
  chatGroups: [],
  triggerSendSuggestionSubject: new Subject<void>(),
  triggerScopeChangeSubject: new Subject<ScopeChangeRequest>(),
  triggerAskAiSubject: new Subject<AskAiInput>(),
  responseFromAiSubject: new Subject<string>(),
  streamingResponseFromAiSubject: new Subject<string>(),
  streamingResponseCompleteSubject: new Subject<void>(),
  cancelInFlightSubject: new Subject<void>(),
  triggerShowConfirmationDialogSubject: new Subject<string>(),
  triggerOpenEntitySubject: new Subject<OpenEntityArgs>(),
  sessionInfo: undefined,
  dataScope: DefaultDataScope,
  sidebarMenuCollapsed: false,
  useAllHouseholds: false,
  promptCategories: [],
  aiResponse: { summary: '' },
  dataProcessingFeatureGroup: {
    admin: createDefaultIqBoostAdminSavedConfiguration(),
    user: createDefaultIqBoostUserSavedConfiguration(),
    isAdmin: false,
  },
  anonymizedInAnimation: {
    animate: false,
    anonymized: false,
  },
  confirmCreateCRMAction: {
    key: '',
    displayText: '',
    title: '',
    description: '',
    action: undefined,
    date: '',
    openDialog: false,
  },
  configurationSettingCodesUser: {
    code3: 'aiIQBoostConfigurationFilters',
    code4: 'filterConfiguration'
  },
  configurationSettingCodesAdmin: {
    code2: CODE_ADMIN,
    code3: 'aiIQBoostConfigurationFilters',
    code4: 'filterConfiguration',
  },
  configurationSettingUser: null,
  configurationSettingAdmin: null,
  feedbackGivenForResponses: new Map(),
  showErrorMessageDialog: false,
  widgetIsExpanded: true,
  accuracyBannerVisible: true,
  outsideQuestion: undefined,
};

export const IQBoostViewStore = signalStore(
  withDevtools('iqboost-view'),
  withState(initialState),
  withComputed((store) => ({
    isBusy: computed(() => store.loading() || store.streaming()),
    streamingStatusKey: computed(() => {
      switch (store.streamingPhase()) {
        case StreamingPhase.GettingContext:
          return 'iqboost.streaming.status.gettingContext';
        case StreamingPhase.GettingContextReadingRecords:
          return 'iqboost.streaming.status.gettingContextReadingRecords';
        case StreamingPhase.GettingContextReadingDocuments:
          return 'iqboost.streaming.status.gettingContextReadingDocuments';
        case StreamingPhase.GettingContextFindingRelevant:
          return 'iqboost.streaming.status.gettingContextFindingRelevant';
        case StreamingPhase.Anonymizing:
          return 'iqboost.streaming.status.anonymizing';
        case StreamingPhase.ProcessingCustomDataSources:
          return 'iqboost.streaming.status.processingCustomDataSources';
        case StreamingPhase.WebSearch:
          return 'iqboost.streaming.status.webSearch';
        case StreamingPhase.Thinking:
          return 'iqboost.streaming.status.thinking';
        default:
          return null;
      }
    }),
  })),
  withMethods((store) => ({
    cancelInFlight(): void {
      store.cancelInFlightSubject().next();
    },
    triggerScopeChange(request: ScopeChangeRequest): void {
      store.cancelInFlightSubject().next();
      store.triggerScopeChangeSubject().next(request);
    },
    triggerResponseFromAi(aiResponse: OpenAI.CompletionResponse | undefined): void {
      const text = aiResponse?.choices
        ?.map((x) => x.message.content).join('\n') ?? '';
      store.responseFromAiSubject().next(text);
    },
    triggerStreamingResponseFromAi(content: string): void {
      if (content.length > 0) {
        patchState(store, () => ({ loading: false, streaming: true, streamingPhase: null }));
      }
      store.streamingResponseFromAiSubject().next(content);
    },
    setStreamingPhase(streamingPhase: StreamingPhase | null): void {
      patchState(store, () => ({ streamingPhase }));
    },
    completeStreamingResponse(): void {
      patchState(store, () => ({ streaming: false, streamingPhase: null }));
      store.streamingResponseCompleteSubject().next();
    },
    triggerShowConfirmationDialog(message: string): void {
      store.triggerShowConfirmationDialogSubject().next(message);
    },
    triggerAskAiEvent(input?: AskAiInput): void {
      const finalInput: AskAiInput = {
        ...input,
        key: input?.key ?? store.key(),
        type: input?.type ?? store.type(),
        invalidateCache: input?.invalidateCache ?? false,
      };

      store.triggerAskAiSubject().next(finalInput);
    },
    triggerOpenEntity(entity: OpenEntityArgs): void {
      store.triggerOpenEntitySubject().next(entity);
    },
    triggerSendSuggestion(): void {
      store.triggerSendSuggestionSubject().next();
    },
    reset(): void {
      patchState(store, () => {
        return {
          key: undefined,
          type: undefined,
          systemPrompts: [],
          currentPrompt: undefined,
          // The catalog is scoped to the entry and the household scope it was fetched for.
          promptCategories: [],
          viewType: ViewType.Report,
          dataScope: DefaultDataScope,
          sidebarMenuCollapsed: false,
          useAllHouseholds: false,
          entryDisplayValue: '',
          opportunityDisplayValue: '',
          outsideQuestion: undefined,
          streaming: false,
          streamingPhase: null,
        }
      });
    },
    setLoading(loading: boolean): void {
      patchState(store, () => ({ loading }));
    },
    setEntryDisplayValue(entryDisplayValue: string): void {
      patchState(store, () => ({ entryDisplayValue }));
    },
    setOpportunityDisplayValue(opportunityDisplayValue: string): void {
      patchState(store, () => ({ opportunityDisplayValue }));
    },
    setUseAllHouseholds(useAllHouseholds: boolean): void {
      patchState(store, () => ({ useAllHouseholds }));
    },
    setPromptCategories(promptCategories: IqBoostPromptCategory[]): void {
      patchState(store, () => ({ promptCategories }));
    },
    setDataScope(dataScope: DataScope): void {
      patchState(store, () => ({ dataScope }));
    },
    setDataProcessingFeatureGroup(dataProcessingFeatureGroup: IQDataProcessingFeaturesGroup): void {
      patchState(store, () => ({ dataProcessingFeatureGroup }));
    },
    setSidebarMenuCollapsed(sidebarMenuCollapsed: boolean): void {
      patchState(store, () => ({ sidebarMenuCollapsed }));
    },
    setCurrentPrompt(currentPrompt?: IqBoostPrompt): void {
      patchState(store, () => ({ currentPrompt }));
    },
    setSystemPrompts(systemPrompts: TextPrompt[]): void {
      patchState(store, () => ({ systemPrompts }));
    },
    setViewType(viewType: ViewType): void {
      patchState(store, () => ({ viewType }));
    },
    setSessionInfo(sessionInfo: Session): void {
      patchState(store, () => ({ sessionInfo }));
    },
    setMaximizerEntry(key: string, type: EntryType): void {
      patchState(store, () => ({
        key,
        type
      }));
    },
    setAiResponse(value: string, type: ViewType): void {
      patchState(store, () => {
        const aiResponse = store.aiResponse();

        if (type === ViewType.Report) {
          aiResponse.summary = value;
        }

        return {
          aiResponse
        };
      });
    },
    setStreamingResponse(content: string): void {
      patchState(store, (state) => ({
        loading: content.length === 0 ? state.loading : false,
        streaming: content.length === 0 ? state.streaming : true,
        streamingPhase: content.length === 0 ? state.streamingPhase : null,
        aiResponse: { ...state.aiResponse, summary: sanitizeMarkdownToHtml(content) },
      }));
    },
    setAnonymizedInAnimation(anonymizedInAnimation: { animate: boolean, anonymized: boolean }): void {
      patchState(store, () => ({ anonymizedInAnimation }));
    },
    setConfirmCreateCRMActionFor(actionFor: string): void {
      patchState(store, () => ({ confirmCreateCRMAction: { ...store.confirmCreateCRMAction(), for: actionFor } }));
    },
    setIsOpenConfirmCreateCRMAction(openDialog: boolean): void {
      patchState(store, () => ({ confirmCreateCRMAction: { ...store.confirmCreateCRMAction(), openDialog } }));
    },
    setConfirmCreateCRMAction(confirmCreateCRMAction: IQBoostCreateCRMAction): void {
      patchState(store, () => ({ confirmCreateCRMAction }));
    },
    setConfigurationSettingUser(configurationSetting: ConfigurationSetting | null): void {
      patchState(store, () => ({ configurationSettingUser: configurationSetting }));
    },
    setConfigurationSettingAdmin(configurationSetting: ConfigurationSetting | null): void {
      patchState(store, () => ({ configurationSettingAdmin: configurationSetting }));
    },
    getFeedbackDisabled(aiResponseText: string): boolean {
      const combinedString = `${aiResponseText}-${store.viewType()}`;
      const hash = SHA256(combinedString);
      return store.feedbackGivenForResponses().get(hash.toString()) ?? false;
    },
    computeFeedbackDisabled(aiResponseText: string): void {
      const combinedString = `${aiResponseText}-${store.viewType()}`;
      const hash = SHA256(combinedString);
      store.feedbackGivenForResponses().set(hash.toString(), true);
    },
    setShowErrorMessageDialog(showErrorMessageDialog: boolean): void {
      patchState(store, () => ({ showErrorMessageDialog }));
    },
    setWidgetIsExpanded(widgetIsExpanded: boolean): void {
      patchState(store, () => ({ widgetIsExpanded }));
    },
    setAccuracyBannerVisible(accuracyBannerVisible: boolean): void {
      patchState(store, () => ({ accuracyBannerVisible }));
    },
    switchToAllHouseholds(): void {
      if (!store.useAllHouseholds()) {
        store.cancelInFlightSubject().next();
        patchState(store, () => ({
          useAllHouseholds: true,
          entryDisplayValue: '',
          opportunityDisplayValue: '',
          outsideQuestion: undefined,
          aiResponse: { summary: '' },
          currentPrompt: undefined,
        }));
        store.triggerScopeChangeSubject().next({
          useAllHouseholds: true,
        });
      }
    },
    setOutsideQuestion(outsideQuestion?: string): void {
      patchState(store, () => ({ outsideQuestion }));
    },
    popOutsideQuestion(): string | undefined {
      const outsideQuestion = store.outsideQuestion();
      if (!isNullOrEmpty(outsideQuestion)) {
        patchState(store, () => ({ outsideQuestion: undefined }));
      }
      return outsideQuestion;
    },
  })),
);

export type IQBoostViewStore = InstanceType<typeof IQBoostViewStore>;
